<template>
  <div class="product-container">
    <product-card v-model:expanded="expanded" :product="product" :usps="uniqueSellingPoints"
      :signup-route="productSignupRoute" :mc-product-id="product.base_id" :mc-cval="product.cval" market="internet"
      :outclick-event-data="outClickEventData" @clickPrice="jumpToTab('pricing')"
      @productClick="$emit('productClick', $event)">
      <template #baseName>
        <span class="base-name">{{ product.base_name }}</span>
      </template>

      <template #headerElements>
        <product-card-icon-element icon="x-fa-download" @click="jumpToTab('internet')">
          <template #title>
            <template v-if="!locationSpeed">
              {{ product.internet.speed_down }} Mb/s
            </template>
            <template v-if="locationSpeed.fullspeed" class="title">
              <span class="expected_speed">{{ product.internet.speed_down }}</span> Mb/s
            </template>
            <template v-else-if="locationSpeed" class="title">
              <span class="expected_speed">{{ locationSpeed.expected }}</span><span>/{{
                product.internet.speed_down
              }}</span> Mb/s
            </template>
          </template>
          <template #subtitle>
            <template v-if="locationSpeed" class="expected_speed_label">
              Download
            </template>
            <template v-else>
              via {{ connectionTypeLabel(product.internet.connection) }}
            </template>
          </template>
        </product-card-icon-element>

        <product-card-icon-element icon="x-fa-upload" @click="jumpToTab('internet')">
          <template #title>
            {{ product.internet.speed_up }} Mb/s
          </template>
          <template #subtitle>
            <span class="expected_speed_label">
              Upload
            </span>
          </template>
        </product-card-icon-element>

        <product-card-icon-element v-if="product.tv.included" icon="tv" @click="jumpToTab('tv')">
          <template #title>
            {{ product.tv.channel_count }} tv-zenders
          </template>

          <template #subtitle>
            {{ product.tv.channel_count_hd }} in HD
          </template>
        </product-card-icon-element>
      </template>

      <template #uspListStart>
        <span v-if="provider_award && product.provider === 't-mobile'" class="usp usp-award">
          <icon icon="trophy-regular" size="m" />
          <span>Beste provider laagste prijs & combivoordeel</span>
        </span>
        <span v-if="provider_award && product.provider === 'kpn'" class="usp usp-award">
          <icon icon="trophy-regular" size="m" />
          <span>Beste provider klanttevredenheid</span>
        </span>
        <span v-if="product.discount_cashback" class="usp cashback-container" @click="jumpToTab('pricing')">
          <span class="attention cashback">
            <icon icon="x-fa-check" size="m" />
            <span>{{ toCurrencyShortEur(product.discount_cashback) }} extra Keuze.nl cashback</span>
            <icon class="extra-info-icon" icon="x-fa-info-circle" size="m" />
          </span>
        </span>
        <span v-if="product.discount_amount_initial === product.base_price_initial" class="usp"
          @click="jumpToTab('pricing')">
          <span class="usp-regular pointer">
            <icon icon="x-fa-check" size="m" />
            <span>Geen aansluitkosten (t.w.v. {{ toCurrencyShortEur(product.base_price_initial) }})</span>
          </span>
        </span>
      </template>

      <template #uspListEnd>
        <span v-if="product.highlight_label" class="usp usp-highlight">
          <span class="highlight">
            <icon :icon="highlightIcon" size="m" />
            <span>{{ product.highlight_label }}</span>
          </span>
        </span>

      </template>

      <template #price>
        <!-- Display price based on sorting and discount availability -->
        <span class="supertitle">
          <!-- Sort monthly price -->
          <template v-if="sort === 'price_monthly' && hasDiscount">
            <span class="offer-price-period ">Vaste prijs</span>
            <span class="subtext-offer mobile-only">Vaste prijs</span>

            <div class="offer-month-price mobile-only">
              <span class="subtext">Per maand</span>
              <span>{{ toCurrencyShortEur(product.total_price) }}</span>
            </div>
            <div class="offer-month-price">
              <span class="subtext">Per maand</span>
              <span>{{ toCurrencyShortEur(product.total_price) }}</span>
            </div>
          </template>

          <!-- Product with discount -->
          <template v-else-if="hasDiscount">
            <span class="offer-price-period">Nu {{ visibleDiscountDuration }} maanden voor</span>
            <span class="subtext-offer mobile-only">Nu voor</span>
            <div class="offer-month-price">
              <span class="subtext">Per maand</span>
              <span>{{ toCurrencyShortEur(product.total_price_discounted) }}</span>
            </div>
            <div class="offer-month-price mobile-only">
              <span class="subtext">{{ visibleDiscountDuration }} maanden</span>
              <span>{{ toCurrencyShortEur(product.total_price_discounted) }}</span>
              <span class="subtext lg">per maand</span>
            </div>
          </template>

          <!-- Default case: no sort, no discount -->
          <template v-else>
            <span class="offer-price-period">Vaste prijs</span>
            <span class="subtext-offer mobile-only">Vaste prijs</span>
            <div class="offer-month-price mobile-only">
              <span class="subtext">Per maand</span>
              <span>{{ toCurrencyShortEur(product.total_price) }}</span>
            </div>
            <div class="offer-month-price">
              <span class="subtext">Per maand</span>
              <span>{{ toCurrencyShortEur(product.total_price) }}</span>
            </div>
          </template>
        </span>

        <!-- Sub-elements for additional pricing details -->
        <span class="subtext subprice_container">
          <!-- Sort monthly price with discount -->
          <template v-if="sort === 'price_monthly' && hasDiscount">
            <div class="subprice_row">
              <span>{{ visibleDiscountDuration }} maanden voor</span>
              <span>{{ toCurrencyShortEur(product.total_price_discounted) }}</span>
            </div>
            <div class="subprice_row" v-if="comparisonStore.filters.contractDuration === '12'">
              <span>Eerste jaar</span>
              <span>{{ toCurrencyShortEur(product.compare_price) }}</span>
            </div>
            <div class="subprice_row" v-else-if="comparisonStore.filters.contractDuration === '24'">
              <span>Gem. jaarprijs</span>
              <span>{{ toCurrencyShortEur(product.compare_price) }}</span>
            </div>
          </template>

          <!-- Sort monthly price without discount -->
          <template v-else-if="sort === 'price_monthly'">
            <div class="subprice_row" v-if="comparisonStore.filters.contractDuration === '12'">
              <span>Eerste jaar</span>
              <span>{{ toCurrencyShortEur(product.compare_price) }}</span>
            </div>
            <div class="subprice_row" v-else-if="comparisonStore.filters.contractDuration === '24'">
              <span>Gem. jaarprijs</span>
              <span>{{ toCurrencyShortEur(product.compare_price) }}</span>
            </div>
          </template>

          <!-- Product with discount and visible discount duration -->
          <template v-else-if="hasDiscount && visibleDiscountDuration > 0">
            <div class="subprice_row">
              <span>Na {{ visibleDiscountDuration }} maanden</span>
              <span>{{ toCurrencyShortEur(product.total_price) }}</span>
            </div>
            <div class="subprice_row" v-if="comparisonStore.filters.contractDuration === '12'">
              <span>Eerste jaar</span>
              <span>{{ toCurrencyShortEur(product.total_price_contract) }}</span>
            </div>
            <div class="subprice_row" v-else-if="comparisonStore.filters.contractDuration === '24'">
              <span>Gem. jaarprijs</span>
              <span>{{ toCurrencyShortEur(product.compare_price) }}</span>
            </div>
          </template>

          <!-- Default case: no discount -->
          <template v-else>
            <div class="subprice_row" v-if="comparisonStore.filters.contractDuration === '12'">
              <span>Eerste jaar</span>
              <span>{{ toCurrencyShortEur(product.compare_price) }}</span>
            </div>
            <div class="subprice_row" v-else-if="comparisonStore.filters.contractDuration === '24'">
              <span>Gem. jaarprijs</span>
              <span>{{ toCurrencyShortEur(product.compare_price) }}</span>
            </div>
          </template>
        </span>
      </template>

      <template #ctaLabel>
        Bekijk abonnement
      </template>

      <template #shippingTime>
        <div class="shipping-time">
          <icon icon="x-fa-truck" size="m" />
          Levertijd {{ product.shipping_time }} dagen
        </div>
      </template>

      <template #productInfo>
        <internet-product-info :product="product" :start-tab="startTab" @jumpToTab="jumpToTab($event)" />
      </template>
    </product-card>
  </div>
</template>
<script setup>
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';

import ProductCard from 'vue-elements/product-card/ProductCard.vue';
import ProductCardIconElement from 'vue-elements/product-card/ProductCardIconElement.vue';
import ProductCardUsp from 'vue-elements/product-card/ProductCardUsp.vue';
import Icon from 'vue-elements/icon/Icon.vue';
import InternetProductInfo from './InternetProductInfo.vue';

import { convertLocationToUrlQuery } from '../../../../../assets/js/helpers/comparison-url.js';
import { useInternetComparisonStore } from '../../stores/internetComparison.js';

const comparisonStore = useInternetComparisonStore();

const props = defineProps({
  product: { type: Object, required: true },
  location: { type: Object, default: null },
  sort: { type: String, default: '' }
});
const emit = defineEmits(['productClick']);

const expanded = ref(false);
const startTab = ref(null);

const router = useRouter();

const highlightIcon = computed(() => {
  switch (props.product.highlight_type) {
    case 'label':
      return 'x-fa-tag';
    case 'star':
      return 'x-fa-star';
    case 'euro':
      return 'x-fa-euro-sign';
    case 'best':
      return 'x-fa-thumbs-up';
    case 'trophy':
      return 'x-fa-trophy';
    default:
      return 'x-fa-tag';
  }
});

const provider_award = computed(() =>
  props.product.provider === 'kpn' || props.product.provider === 't-mobile'
);

const uniqueSellingPoints = computed(() => {
  let uspsCount = 3;
  const usps = [];

  if (typeof props.product !== 'undefined') {
    if (props.product.offer) {
      if (
        props.product.discount_amount_initial == props.product.base_price_initial &&
        !(props.product.offer.includes('aansluit'))
      ) {
        usps.push(['x-fa-gift', props.product.offer]);
      }
    }

    if (typeof props.product.phone !== 'undefined' && props.product.phone.included) {
      usps.push(['x-fa-check', 'Inclusief vaste telefoon']);
    }

    if (props.product.installation_support === 'included' && usps.length < 3) {
      usps.push(['x-fa-check', 'Gratis installatiemonteur']);
    }

    props.product.tv.enabled.forEach((tvProduct) => {
      if (
        tvProduct.price_monthly === 0 &&
        !tvProduct.name.includes('tandaard') &&
        !tvProduct.name.includes('asispakket')
      ) {
        let tvProductName = tvProduct.name;
        tvProductName = tvProductName.replace('Gratis', '');
        tvProductName = tvProductName.replace('gratis', '');
        usps.push(['x-fa-check', `Gratis ${tvProductName}`]);
      }
    });

    if (
      typeof props.product.internet !== 'undefined' &&
      props.product.internet.connection === 'fiber'
    ) {
      usps.push(['x-fa-check', 'Glasvezel internet']);
    } else if (usps.length < 2) {
      usps.push(['x-fa-check', 'Gratis draadloos modem']);
    }

    if (
      usps.length < 2 &&
      props.product.installation_support !== 'included' &&
      !props.product.discount_text
    ) {
      usps.push(['x-fa-check', 'Doe-het-zelf installatiepakket']);
    }
  }

  if (props.product.highlight_label) {
    uspsCount--;
  }

  if (props.product.discount_amount_initial === props.product.base_price_initial) {
    uspsCount--;
  }

  if (props.product.discount_cashback) {
    uspsCount--;
  }

  if (props.product.offer) {
    uspsCount++;
  }

  if (provider_award.value) {
    uspsCount--;
  }

  if (props.product.offer && !props.product.highlight_label) {
    uspsCount--;
  }

  return usps.splice(0, uspsCount);
});

const locationSpeed = computed(() => {
  if (!props.location || props.product.internet.speed_down_expected < 1) {
    return false;
  }
  return {
    expected: props.product.internet.speed_down_expected,
    fullspeed: props.product.internet.speed_down === props.product.internet.speed_down_expected,
  };
});

const productSignupRoute = computed(() =>
  router.resolve({
    name: 'productLink',
    params: {
      provider: props.product.provider,
      productID: props.product.base_id,
    },
    query: {
      ...convertLocationToUrlQuery(props.location),
    },
  }).href
);

const outClickEventData = computed(() => ({
  contract_duration: props.product.contract_duration,
  product_name: props.product.base_name,
  network_type: props.product.internet.connection,
  speed_down: props.product.internet.speed_down,
  speed_down_expected: props.product.internet.speed_down_expected,
  package_type: props.product.package_type,
  price: props.product.total_price,
}));

const hasDiscount = computed(() => {
  const hasInternetDiscount = props.product.discount_duration > 0;

  const hasTvDiscount =
    props.product.tv.included &&
    props.product.tv.discount_price != null &&
    props.product.tv.discount_price < props.product.tv.monthly_price;

  const hasPhoneDiscount =
    props.product.phone.included &&
    props.product.phone.discount_price != null &&
    props.product.phone.discount_price < props.product.phone.monthly_price;

  return hasInternetDiscount || hasTvDiscount || hasPhoneDiscount;
});

const visibleDiscountDuration = computed(() => {
  const internetDiscount = props.product.discount_duration || 0;
  const tvDiscount = props.product.tv.included ? props.product.tv.discount_duration || 0 : 0;
  const phoneDiscount = props.product.phone.included ? props.product.phone.discount_duration || 0 : 0;

  if (internetDiscount === 0 && tvDiscount === 0 && phoneDiscount === 0) {
    return 0;
  }

  if (internetDiscount > 0 && tvDiscount === 0 && phoneDiscount === 0) {
    return internetDiscount;
  } else if (tvDiscount > 0 && internetDiscount === 0 && phoneDiscount === 0) {
    return tvDiscount;
  } else if (phoneDiscount > 0 && internetDiscount === 0 && tvDiscount === 0) {
    return phoneDiscount;
  } else {
    return Math.min(
      internetDiscount > 0 ? internetDiscount : Infinity,
      tvDiscount > 0 ? tvDiscount : Infinity,
      phoneDiscount > 0 ? phoneDiscount : Infinity
    );
  }
});

watch(expanded, (newVal) => {
  if (newVal === true) {
    // If you have a global analytics function, call it here
    if (typeof logAnalyticsEvent === 'function') {
      logAnalyticsEvent('compare-internet.more-information');
    }
  }
});

function jumpToTab(tab) {
  startTab.value = tab;
  expanded.value = true;
}
</script>
<style lang="scss">
@use '../../../../../abstracts_depr/elements/button/no-icon/button-no-icon' as button_inactive;
@use '../../../../../assets/scss/_variables/colors' as colors;
@use '../../../../../assets/scss/_helpers/typography' as typography;
@use '../../../../../assets/scss/_helpers/border' as border;
@use '../../../../../assets/scss/_helpers/display' as display;
@use '../../../../../assets/scss/_helpers/breakpoint' as breakpoint;
@import '../../style/variables';
@import '../../../../elements/link/button/primary/link';

.bottom-bar {
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #c4e3ed;
  padding-top: 12px;

  grid-column: 1 /3;

  .icon-element {
    margin-right: 4px;
    width: 21px;
    height: 21px;
  }

  .expiring {
    color: #ff0000;

    .icon-element {
      background-color: #ff0000;
    }
  }

  .delivery {
    color: $discount-color;

    .icon-element {
      background-color: $discount-color;
    }
  }
}

.product-container {
  .cashback {
    cursor: pointer;
  }
}

.product {
  .button-element {
    white-space: nowrap;
  }
}


.row.product-card-icon-row {
  align-items: center;
}

.product .more {
  &>span:first-child {
    margin-right: 8px;
  }
}

.product .usp-attention {
  grid-row: 1;
}

.product .usp-attention .attention {

  background-color: colors.$green-20;
  color: colors.$green-60;
  border: 1px solid colors.$green-60;
  margin-bottom: 4px;

  cursor: pointer;

  .extra-info-icon {
    background-color: colors.$green-60;
  }
}

.product .usps .attention {
  margin-bottom: 8px;
  background-color: colors.$green-20;
  color: colors.$green-60;
  border: 1px solid colors.$green-60;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 8px;

  .icon-element,
  &.cashback .icon-element {
    background-color: colors.$green-50;
    margin-right: 6px;
  }

  .icon-element,
  &.cashback .extra-info-icon {
    background-color: colors.$green-50;
    margin-left: 6px;
  }
}

.product.internet {
  .usp-present {
    .icon-element {
      margin-left: 8px;
    }
  }

  .usps .usp-award {
    cursor: auto;
    gap: 8px;
    background-color: #FFE99F;
    border: 1px solid #E4C24E;
    padding: 6px 12px;
    border-radius: 8px;
    width: fit-content;

    .icon-element {
      background: colors.$border-promotion;
    }
  }
}

//// Opzet voor scheiden internet en energie card
// Internet product card

.product.internet {
  border: 1px solid colors.$border-base;
  border-bottom-width: 3px;
  grid-template-columns: 1fr 1.5fr;
  background: colors.$element-base;
  padding: 16px;
  gap: 16px;
  border-radius: 0;

  @include breakpoint.up(sm) {
    border-radius: 15px;
  }

  @include breakpoint.up(md) {
    padding: unset;
    grid-template-columns: auto 1fr 0.5fr;
    grid-template-rows: auto auto auto auto;
    background: colors.$grey-10;
    gap: unset;
  }

  @include breakpoint.up(lg) {
    grid-template-columns: 1.5fr 1fr;
    background: colors.$element-base;
    padding: 24px 24px 24px 24px;
    gap: 16px;
  }

  @include breakpoint.up(xl) {
    padding: unset;
    grid-template-columns: auto 1fr 0.5fr;
    background: colors.$grey-10;
    gap: unset;
  }

  // Provider logo
  .provider_logo {
    @include display.flex-col-center();

    grid-row: 1;
    grid-column: 1;
    align-items: flex-start;
    justify-content: flex-start;

    @include breakpoint.down(md) {
      margin-top: 8px;
    }

    @include breakpoint.up(md) {
      background: colors.$element-base;
      padding: 24px 16px 16px 24px;
      justify-content: center;
    }

    @include breakpoint.up(lg) {
      background: unset;
      padding: unset;
    }

    @include breakpoint.up(xl) {
      background: colors.$element-base;
      padding: 32px 16px 16px 32px;
    }

    img {
      width: 85px;
      object-fit: contain;
      max-height: 58px
    }
  }

  // Pakket naam
  .base_name {
    display: none;
    cursor: default;

    @include breakpoint.up(md) {
      @include display.flex-col-center();
      @include typography.header-6();

      background: colors.$element-base;
      align-items: flex-start;
      padding: 24px 24px 16px 0;
    }

    @include breakpoint.up(lg) {
      display: none;
    }

    @include breakpoint.up(xl) {
      display: flex;
      padding: 24px 24px 16px 0;
    }
  }

  // Pakket specificaties
  .elementcontainer {
    grid-row: 2;
    grid-column: 1/ span 2;

    @include breakpoint.up(md) {
      padding-bottom: 12px;
      background: colors.$element-base;
    }


    .row {
      display: grid;
      grid-template-columns: 1fr 1fr 1.2fr;
      padding: 12px 8px;
      border-top: 1px solid colors.$border-base;
      border-bottom: 1px solid colors.$border-base;
      padding: 8px 12px;
      margin: 0;

      @include breakpoint.up(xl) {
        grid-template-columns: 1fr 1fr 1.2fr;
        gap: unset;
      }
    }

    .elementgroup {
      @include display.flex-row-start();

      color: colors.$text-base;
      align-items: flex-start;
      gap: 8px;
      cursor: pointer;
      padding: 0px;

      .elementvalue {
        @include display.flex-col-start();
      }

      span {
        @include typography.content-small();

        @include breakpoint.up(md) {
          @include typography.content-short();
        }
      }

      .title,
      .expected_speed {
        @include typography.content-small-strong();

        @include breakpoint.up(md) {
          @include typography.content-short-strong();
        }
      }
    }
  }

  .icon-container {
    background-color: transparent;
    align-items: flex-start;

    .icon-element {
      background-color: colors.$text-base;
      width: 14px;
      height: 14px;

      @include breakpoint.up(md) {
        margin-top: 4px;
        width: 16px;
        height: 16px;
      }
    }
  }

  // Prijs container
  .price {
    grid-column: 2;
    grid-row: 1;

    @include breakpoint.up(md) {
      grid-column: 3;
      grid-row: 1 / span 2;
      padding: 24px;
      border-left: 1px solid colors.$border-base;
    }

    @include breakpoint.up(lg) {
      grid-column: 2;
      grid-row: 1;
      padding: unset;
      border-left: unset;
    }

    @include breakpoint.up(xl) {
      grid-column: 3;
      grid-row: 1 / span 2;
      padding: 24px;
      border-left: 1px solid colors.$border-base;
    }
  }

  .price-container {
    width: 100%;
    gap: 56px;
    cursor: pointer;

    @include breakpoint.up(md) {
      gap: 62px;
    }

    @include breakpoint.up(lg) {
      gap: 56px;
    }

    @include breakpoint.up(xl) {
      gap: 62px;
    }

    .supertitle {
      @include display.flex-col-end();

      width: 100%;

      @include breakpoint.up(lg) {
        .offer-price-period {
          margin-right: 24px;
        }
      }

      @include breakpoint.up(xl) {
        position: unset;
        top: unset;
        right: unset;

        .offer-price-period {
          margin-right: unset;
        }
      }

      &.non-deal {
        .offer-month-price {
          display: flex;

          @include breakpoint.down(md) {
            position: absolute;
            width: 60%;
            right: 0px;
          }

          @include breakpoint.up(md) {
            width: 235px;
          }

          @include breakpoint.up(lg) {
            width: unset;
          }

          @include breakpoint.up(xl) {
            width: 235px;
          }
        }

        @include breakpoint.up(lg) {
          top: 28px;
        }

        .subprice_container {
          padding-top: 46px;
        }
      }
    }

    .subtext-offer {
      @include typography.content-small-strong();

      @include breakpoint.up(lg) {
        margin-right: 16px;
        margin-bottom: 4px;
      }

      &.mobile-only {
        margin-bottom: 8px;

        @include breakpoint.up(md) {
          display: none;
        }

        @include breakpoint.up(lg) {
          display: block;
        }

        @include breakpoint.up(xl) {
          display: none;
        }
      }
    }

    .offer-price-period {
      display: none;

      @include breakpoint.up(md) {
        @include typography.content-small-strong();

        display: block;
        line-height: 24px;
      }

      @include breakpoint.up(lg) {
        display: none;
      }

      @include breakpoint.up(xl) {
        display: block;
      }

    }

    .non-deal .offer-month-price {
      top: 16px;

      @include breakpoint.up(md) {
        top: 24px;
        flex-direction: row;
      }

      @include breakpoint.up(lg) {
        flex-direction: row-reverse;
        text-transform: lowercase;
      }

      @include breakpoint.up(xl) {
        flex-direction: row;
        text-transform: none;
      }

      .subtext {
        display: block;
      }
    }

    .offer-month-price {
      @include display.flex-row-between();

      display: none;
      font-size: 20px;
      font-weight: 700;
      ;
      line-height: 20px;
      border-radius: 5px 0 0 5px;
      padding: 8px 16px;
      background: colors.$element-yellow-60;
      border-width: 1px 0 1px 1px;
      border-style: solid;
      border-color: colors.$border-promotion;
      gap: 8px;

      @include breakpoint.up(md) {
        @include typography.header-5();

        display: flex;
        position: absolute;
        top: 50px;
        right: 0px;
        padding: 6px 24px 6px 36px;
        width: 248px;
      }

      @include breakpoint.up(lg) {
        display: none;
        position: unset;
        top: unset;
        right: unset;
        padding: 6px 24px 6px 16px;
        width: unset;
      }

      @include breakpoint.up(xl) {
        display: flex;
        position: absolute;
        top: 50px;
        right: 0px;
        padding: 6px 24px 6px 36px;
        width: 248px;
      }

      &.mobile-only {
        position: absolute;
        right: 0px;
        width: 60%;
        top: 40px;

        .subtext {
          display: block;
        }

        .subtext.lg {
          display: none;
        }

        @include breakpoint.up(md) {
          display: none;

          .subtext.lg {
            display: none;
          }
        }

        @include breakpoint.up(lg) {
          display: flex;
          width: 45%;
          top: 50px;
        }

        @include breakpoint.up(xl) {
          display: none;
        }

        @include breakpoint.up(xl) {
          .subtext.lg {
            display: block;
          }
        }
      }
    }

    .subtext {
      @include typography.content-small();
      color: colors.$text-base;
      display: none;

      @include breakpoint.up(md) {
        display: block;
      }

      @include breakpoint.up(lg) {
        display: none;
      }

      @include breakpoint.up(xl) {
        display: block;
      }

      &.mobile-only {
        display: block;

        @include breakpoint.up(md) {
          display: none;
        }

        @include breakpoint.up(lg) {
          display: block;
        }

        @include breakpoint.up(xl) {
          display: none;
        }
      }
    }

    .subprice_container {
      @include display.flex-col-end();

      width: 100%;
      gap: 8px;
    }

    .subprice_row {
      @include typography.content-small();
      @include display.flex-row-between();

      width: 100%;

      &:not(:last-child) {
        padding-bottom: 8px;
        border-bottom: 1px solid colors.$border-base (
        );
    }
  }
}

.subprice_row.mobile-only,
.divider.mobile-only,
.offer-month-price.mobile-only {
  display: flex;

  @include breakpoint.up(md) {
    display: none;
  }

  @include breakpoint.up(lg) {
    display: flex;
  }

  @include breakpoint.up(xl) {
    display: none;
  }
}

.cta_container {
  @include display.flex-row-between();

  gap: 8px;
  grid-column: 1 / -1;
  grid-row: 4;
  justify-content: space-between;
  flex-direction: row-reverse;

  @include breakpoint.up(md) {
    margin-bottom: 4px;
    @include display.flex-col-center();

    grid-column: 3;
    grid-row: 3;
    padding: 0px 24px 24px 24px;
    border-left: 1px solid colors.$border-base;
    justify-content: flex-end;
    margin-bottom: 0px;
  }

  @include breakpoint.up(lg) {
    grid-column: 1 / -1;
    grid-row: 4;
    padding: unset;
    border-left: unset;
    justify-content: space-between;
    flex-direction: row-reverse;
  }

  @include breakpoint.up(xl) {
    grid-column: 3;
    grid-row: 3;
    padding: 0px 24px 24px 24px;
    border-left: 1px solid colors.$border-base;
    justify-content: flex-end;
    flex-direction: column;
  }

  .shipping-time {
    @include display.flex-row-center();
    @include typography.paragraph-14;
    width: 100%;
    color: colors.$green-50;
    gap: 4px;
    font-weight: 600;

    .icon-element {
      background: currentColor;
      margin-top: 4px;
      height: 16px;
      width: 16px;
    }
  }
}

// Usp list
.usps {
  grid-column: 1/ span 2;
  grid-row: 3;

  @include breakpoint.up(md) {
    background: colors.$element-base;
    padding: 0 24px 63px 24px;
  }

  @include breakpoint.up(lg) {
    padding: unset;
  }

  @include breakpoint.up(xl) {
    padding: 0 24px 24px 24px;
  }


}

.element.more-info {
  background-color: colors.$element-base;
}

.usp-list {
  row-gap: 4px;

  @include breakpoint.down(md) {
    padding-bottom: 16px;
    border-bottom: 1px solid colors.$border-base;
  }

  @include breakpoint.up(lg) {
    padding-bottom: 16px;
    border-bottom: 1px solid colors.$border-base;
  }

  @include breakpoint.up(xl) {
    padding-bottom: unset;
    border-bottom: unset;
  }

  .icon-element {
    margin: 0px;
    height: 100%;
  }
}

.usp .usp-regular,
.usp.usp-regular,
.usp.cashback-container,
.usp.usp-award,
.usp-present {
  @include typography.paragraph-15();
  @include display.flex-row-start();
  gap: 6px;
  cursor: unset;

  @include breakpoint.up(md) {
    @include typography.paragraph-17();
  }

}

.usp .usp-regular.pointer {
  cursor: pointer;
}

// USP HIGHLIGHT
.usp.usp-highlight {
  grid-row: 1;

  .highlight {
    @include display.flex-row-start();

    padding: 6px 12px;
    border-radius: 8px;
    border: 1px solid #E4C24E;
    background: #FFE99F;
    width: fit-content;
    gap: 6px;



    .icon-element {
      background-color: #BB920A;
    }
  }
}

// Meer informatie balk (desktop styling in productcard.vue)
.more {
  @include display.flex-row-center();

  grid-row: 5;
  grid-column: 1 / -1;
  background: colors.$background-default;
  border-top: 1px solid colors.$border-base;
  padding-top: 16px;
  font-weight: 600;
  color: colors.$link-base-default;
  cursor: pointer;

  @include breakpoint.up(md) {
    padding: 8px 0;
  }
}

}

//// Invalid postal code
// Product cards
.inactive {
  .product.internet {
    box-shadow: unset;
    color: colors.$text-inactive;

    position: relative;

    &::after {
      content: '';
      cursor: not-allowed;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 1;
      background-color: rgba(255, 255, 255, 0.5);
      backdrop-filter: grayscale(1);
    }

  }
}
</style>
